// ABOUTME: Admin family management component displaying paginated list of families with search and navigation capabilities
// ABOUTME: Provides complete family management interface for administrators including family details and behavior tracking

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  TablePagination,
  CircularProgress,
  Alert,
  Button,
  InputAdornment,
  Chip,
  Container,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  ArrowBack,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { getAllFamilies } from '../services/api';
import { formatDistanceToNow, parseISO } from 'date-fns';
import SoftDeleteToggle, { SoftDeleteFilter } from './SoftDeleteToggle';
import { getSubscriptionStatusColor, formatSubscriptionStatus } from '../utils/subscriptionUtils';

interface FamilyData {
  id: number;
  name: string;
  createdAt: string;
  lastActivityAt?: string;
  adultCount: number;
  childrenCount: number;
  primaryAdultName: string;
  subscriptionStatus?: string;
  isDeleted: boolean;
}

interface FamilyListResponse {
  families: FamilyData[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

type SortField = 'name' | 'createdAt' | 'lastActivityAt' | 'adultCount' | 'childrenCount';

interface SortConfig {
  field: SortField;
  direction: 'asc' | 'desc';
}

const AdminFamilyManagement: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [families, setFamilies] = useState<FamilyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'lastActivityAt', direction: 'desc' });
  const [softDeleteFilter, setSoftDeleteFilter] = useState<SoftDeleteFilter>('active');
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState<string[]>([]);

  const fetchFamilies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const showDeletedParam = softDeleteFilter === 'deleted' ? 'deleted' :
                             softDeleteFilter === 'all' ? 'all' : undefined;

      const subscriptionStatusParam = subscriptionStatusFilter.length > 0
        ? subscriptionStatusFilter.join(',')
        : undefined;

      const response: FamilyListResponse = await getAllFamilies(
        page + 1,
        pageSize,
        searchTerm || undefined,
        sortConfig.field,
        sortConfig.direction === 'desc',
        showDeletedParam,
        subscriptionStatusParam
      );

      setFamilies(response.families);
      setTotalCount(response.totalCount);
    } catch (err) {
      console.error('Error fetching families:', err);
      setError('Error loading families');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, sortConfig, softDeleteFilter, subscriptionStatusFilter]);

  useEffect(() => {
    fetchFamilies();
  }, [fetchFamilies]);

  const handleSort = (field: SortField) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setPage(0); // Reset to first page when sorting
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (familyId: number) => {
    navigate(`/admin/families/${familyId}`);
  };

  const handleRefresh = () => {
    fetchFamilies();
  };

  const formatLastActivity = (lastActivityAt?: string) => {
    if (!lastActivityAt) return 'No activity';
    try {
      // parseISO correctly handles UTC timestamps with 'Z' suffix
      // This ensures admins see consistent elapsed time regardless of their timezone
      const utcDate = parseISO(lastActivityAt);
      return formatDistanceToNow(utcDate, { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };

  const formatCreatedDate = (createdAt: string) => {
    try {
      return new Date(createdAt).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  if (loading && families.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress data-testid="families-loading" />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin')}
          sx={{ mr: 2 }}
          data-testid="back-button"
        >
          Back to Admin
        </Button>
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            Family Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage families and view family activity across the Let&apos;s Reset platform
          </Typography>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
          <Button onClick={fetchFamilies} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}

      {/* Main Content */}
      <Paper sx={{ p: 3 }}>
        <Box data-testid="family-table-container" sx={{ position: 'relative' }}>
          {/* Soft Delete Filter Toggle */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" component="h2">
              Families ({totalCount})
            </Typography>
            <SoftDeleteToggle
              value={softDeleteFilter}
              onChange={(newFilter) => {
                setSoftDeleteFilter(newFilter);
                setPage(0); // Reset to first page
              }}
              disabled={loading}
            />
          </Box>

          {/* Subscription Status Filter */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Filter by Subscription Status:
            </Typography>
            <FormGroup row>
              {['trial', 'active', 'cancelled', 'expired', 'paused', 'refunded', 'past_due', 'free'].map((status) => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      checked={subscriptionStatusFilter.includes(status)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSubscriptionStatusFilter([...subscriptionStatusFilter, status]);
                        } else {
                          setSubscriptionStatusFilter(subscriptionStatusFilter.filter(s => s !== status));
                        }
                        setPage(0); // Reset to first page
                      }}
                      disabled={loading}
                    />
                  }
                  label={status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                />
              ))}
            </FormGroup>
          </Box>

          {/* Search and Refresh */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search families..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              data-testid="search-input"
            />
            <IconButton onClick={handleRefresh} data-testid="refresh-button">
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Families Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.field === 'name'}
                      direction={sortConfig.field === 'name' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Family Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.field === 'createdAt'}
                      direction={sortConfig.field === 'createdAt' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('createdAt')}
                    >
                      Creation Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.field === 'lastActivityAt'}
                      direction={sortConfig.field === 'lastActivityAt' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('lastActivityAt')}
                    >
                      Last Activity
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortConfig.field === 'adultCount'}
                      direction={sortConfig.field === 'adultCount' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('adultCount')}
                    >
                      Adults
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortConfig.field === 'childrenCount'}
                      direction={sortConfig.field === 'childrenCount' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('childrenCount')}
                    >
                      Children
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Primary Adult</TableCell>
                  <TableCell>Subscription Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {families.map((family) => {
                  const isDeleted = family.isDeleted;
                  const isGreyedOut = softDeleteFilter === 'all' && isDeleted;
                  
                  return (
                    <TableRow
                      key={family.id}
                      hover
                      onClick={() => handleRowClick(family.id)}
                      sx={{ 
                        cursor: 'pointer',
                        opacity: isGreyedOut ? 0.5 : 1,
                        color: isGreyedOut ? theme.palette.grey[500] : 'inherit'
                      }}
                      data-testid={`family-row-${family.id}`}
                    >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="subtitle2" 
                          fontWeight="medium"
                          sx={{
                            color: isGreyedOut ? theme.palette.grey[500] : 'inherit',
                            textDecoration: isDeleted && softDeleteFilter === 'all' ? 'line-through' : 'none'
                          }}
                        >
                          {family.name}
                        </Typography>
                        {isDeleted && (
                          <Chip 
                            label="DELETED" 
                            size="small" 
                            color="error" 
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {formatCreatedDate(family.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatLastActivity(family.lastActivityAt)}
                        size="small"
                        color={family.lastActivityAt ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={family.adultCount} size="small" color="info" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={family.childrenCount} size="small" color="success" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {family.primaryAdultName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {family.subscriptionStatus ? (
                        <Chip
                          label={formatSubscriptionStatus(family.subscriptionStatus)}
                          size="small"
                          color={getSubscriptionStatusColor(family.subscriptionStatus)}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Unknown
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {families.length === 0 && !loading && (
            <Box p={4} textAlign="center">
              <Typography variant="body1" color="text.secondary">
                {searchTerm ? 'No families found matching your search.' : 'No families found.'}
              </Typography>
            </Box>
          )}

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default AdminFamilyManagement;